import React, { ReactElement, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TableContainer,
  Typography,
  Checkbox,
  Avatar,
  MenuItem
} from '@mui/material'
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignmentIcon,
  Done as DoneIcon,
  ArrowUpward,
  ArrowDownward,
  Tune as TuneIcon,
  VerticalAlignBottom as ResultIcon,
  SupervisedUserCircle as ReassignUserIcon
} from '@mui/icons-material'
import { Pagination } from '../../../components/Pagination'
import { Loading } from '../../UI'
import Menu from '@mui/material/Menu'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  ActionProps,
  GenericTableProps,
  PrioritizationActionProps,
  TableData
} from './types'
import { getImage } from './helpers'

// Props for the generic ReturnsTable component
export const GenericTable = <T extends TableData>({
  data = [],
  pagination,
  onPaginationChange,
  headersConfig,
  rowConfig = [],
  actions,
  // checkbox
  showCheckboxes = false,
  onCheckChange,
  checkedElements = [],
  onCheckAll,
  // dense style
  dense = false,
  IDKey, // the key of the ID
  customActionComponent: CustomActionComponent, // Destructure customActionComponent from props
  isLoading,
  onSortChange,
  orderField,
  showPriorityMenu = false,
  priorization,
  noResultsText,
  priorizationNumberKey
}: GenericTableProps<T>) => {
  const styles = useStyles()

  // Checkear todos
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onCheckAll) {
      const isChecked = event.target.checked
      // Notifica al componente padre para que maneje la lógica de selección
      if (isChecked) {
        // Si el checkbox "seleccionar todo" está marcado, el componente padre debe seleccionar todos los elementos visibles
        onCheckAll(true)
      } else {
        // Si el checkbox "seleccionar todo" está desmarcado, el componente padre debe deseleccionar todos los elementos
        onCheckAll(false)
      }
    }
  }

  const renderActionsCell = (id: number, row: T) => {
    if (CustomActionComponent) {
      return (
        <TableCell align='center'>
          <div className={styles.action}>
            <CustomActionComponent id={id} row={row} />
          </div>
        </TableCell>
      )
    }

    // Otherwise, render default edit and delete buttons
    const {
      onDelete,
      onEdit,
      onView,
      onConfirm,
      onTune,
      onResult,
      onReassign
    } = actions as ActionProps<T>

    return (
      <TableCell align='center'>
        <div className={styles.action}>
          {onTune && (
            <IconButton onClick={() => onTune(id, row)}>
              <TuneIcon />
            </IconButton>
          )}
          {onEdit && (
            <IconButton onClick={() => onEdit(id, row)}>
              <EditIcon />
            </IconButton>
          )}
          {onReassign && (
            <IconButton onClick={() => onReassign(id, row)}>
              <ReassignUserIcon />
            </IconButton>
          )}
          {onView && (
            <IconButton onClick={() => onView(id, row)}>
              <AssignmentIcon />
            </IconButton>
          )}
          {onDelete && (
            <IconButton onClick={() => onDelete(id, row)}>
              <DeleteIcon />
            </IconButton>
          )}
          {onConfirm && (
            <IconButton onClick={() => onConfirm(id, row)}>
              <DoneIcon />
            </IconButton>
          )}
          {onResult && (
            <IconButton onClick={() => onResult(id, row)}>
              <ResultIcon />
            </IconButton>
          )}
        </div>
      </TableCell>
    )
  }

  const renderCheckboxCell = (id: number) => {
    return (
      <TableCell align='center'>
        <Checkbox
          checked={checkedElements.includes(id)} // You should use the actual checked state based on your data
          onChange={(event) => {
            const checked = event.target.checked
            if (onCheckChange) {
              onCheckChange(id, checked)
            }
          }}
        />
      </TableCell>
    )
  }

  const renderCellValue = (
    value: T[keyof T],
    formater?: (value: T[keyof T], row?: T) => string | ReactElement,
    isImage?: boolean,
    row?: T
  ): React.ReactNode => {
    if (formater !== undefined) {
      return formater(value, row)
    }

    // Check if the field type is boolean and handle accordingly
    if (typeof value === 'boolean') {
      return value ? 'Activo' : 'Inactivo'
    }

    if (isImage !== undefined) {
      const imageValue = typeof value === 'string' ? value : ''

      return (
        <Avatar
          className={styles.imageLarge}
          variant='square'
          alt='SkuImage'
          src={getImage(imageValue)}
        />
      )
    }

    return value
  }

  // PRIORIDAD
  const [anchorEls, setAnchorEls] = useState<{
    [key: string]: HTMLElement | null
  }>({})
  // const [selectedRow, setSelectedRow] = React.useState<T | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, row: T) => {
    setAnchorEls((prevAnchorEls) => ({
      ...prevAnchorEls,
      [row[IDKey]]: event.currentTarget
    }))
  }

  const handleClose = (rowId: string) => {
    setAnchorEls((prevAnchorEls) => ({ ...prevAnchorEls, [rowId]: null }))
  }

  const calculaElNumero = (
    rowIndex: number,
    paginaActual: number,
    tamañoDePagina: number
  ) => {
    const startIndex = paginaActual * tamañoDePagina
    return startIndex + rowIndex + 1 // Suma 1 porque los índices comienzan en 0
  }

  const renderPriorityMenu = (row: T) => {
    const { onTop, onUp, onDown } = priorization as PrioritizationActionProps<T>

    let priorityNumber = 0

    if (priorizationNumberKey) {
      priorityNumber = row[priorizationNumberKey]
    } else {
      priorityNumber = calculaElNumero(
        data.indexOf(row),
        pagination?.page || 0,
        pagination?.pageSize || 0
      )
    }

    return (
      <TableCell align='center'>
        <IconButton
          aria-label='more'
          aria-controls={`priority-menu-${row[IDKey]}`}
          aria-haspopup='true'
          onClick={(event) => handleClick(event, row)}
        >
          {priorityNumber > 0 && <span>{priorityNumber}</span>}
          <MoreVertIcon />
        </IconButton>
        <Menu
          id={`priority-menu-${row[IDKey]}`}
          anchorEl={anchorEls[row[IDKey]]}
          keepMounted
          open={Boolean(anchorEls[row[IDKey]])}
          onClose={() => handleClose(row[IDKey])}
        >
          {onTop && (
            <MenuItem
              onClick={() => {
                onTop(row[IDKey], row)
                handleClose(row[IDKey])
              }}
            >
              Mover al principio
            </MenuItem>
          )}
          {onUp && (
            <MenuItem
              onClick={() => {
                onUp(row[IDKey], row)
                handleClose(row[IDKey])
              }}
            >
              Mover arriba
            </MenuItem>
          )}
          {onDown && (
            <MenuItem
              onClick={() => {
                onDown(row[IDKey], row)
                handleClose(row[IDKey])
              }}
            >
              Mover abajo
            </MenuItem>
          )}
        </Menu>
      </TableCell>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.empty}>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  if (data.length === 0) {
    if (noResultsText) {
      return (
        <div className={styles.empty}>
          <Typography>{noResultsText}</Typography>
        </div>
      )
    }

    return (
      <div className={styles.empty}>
        <Typography>
          Ajuste los criterios de busqueda para desplegar resultados
        </Typography>
      </div>
    )
  }

  return (
    <TableContainer>
      <Table size={dense ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            {showCheckboxes && (
              <TableCell align='center'>
                <Checkbox
                  indeterminate={
                    checkedElements.length > 0 &&
                    checkedElements.length < data.length
                  }
                  checked={
                    checkedElements.length === data.length && data.length > 0
                  }
                  onChange={handleSelectAllClick}
                />
              </TableCell>
            )}
            {/* {showCheckboxes && (
              <TableCell align='center' style={{ width: '50px' }} />
            )} */}
            {showPriorityMenu && (
              <TableCell align='center' style={{ width: '50px' }} />
            )}
            {headersConfig.map((config) => (
              <TableCell
                key={config.field}
                align='center'
                style={{ ...config.headStyle, position: 'relative' }}
                onClick={() => {
                  config.sortable &&
                    onSortChange &&
                    onSortChange({
                      field: config.fieldReplacement || config.field,
                      direction: 'asc'
                    })
                }}
                className={`${styles.tableHeaderCell} ${
                  config.sortable ? styles.sortable : ''
                } ${
                  orderField?.field === config.field && config.sortable
                    ? styles.selected
                    : ''
                }`}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  <span>{config.title}</span>
                  {orderField?.field === config.field && config.sortable && (
                    <span>
                      {orderField.direction === 'asc' ? (
                        <ArrowUpward fontSize='small' />
                      ) : (
                        <ArrowDownward fontSize='small' />
                      )}
                    </span>
                  )}
                </div>
              </TableCell>
            ))}
            {(actions || CustomActionComponent) && (
              <TableCell align='center' style={{ width: '50px' }}>
                Acciones
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((rowData, index) => {
            return (
              <TableRow
                key={rowData[IDKey]}
                style={{
                  height: dense ? 33 : 53,
                  ...(rowConfig[index] as React.CSSProperties)
                }}
              >
                {showCheckboxes && renderCheckboxCell(rowData[IDKey])}
                {showPriorityMenu && renderPriorityMenu(rowData)}
                {headersConfig.map((config) => (
                  <TableCell
                    key={config.field}
                    align='center'
                    style={config.style}
                  >
                    {renderCellValue(
                      rowData[config.field],
                      config.formater,
                      config.isImage,
                      rowData
                    )}
                  </TableCell>
                ))}
                {actions && renderActionsCell(rowData[IDKey], rowData)}
                {CustomActionComponent &&
                  renderActionsCell(rowData[IDKey], rowData)}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {data.length === 0 && (
        <div className={styles.empty}>
          <Typography>Ajuste los criterios de busqueda</Typography>
        </div>
      )}
      <Pagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
      />
    </TableContainer>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  action: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '10vh'
  },
  tableHeaderCell: {},
  sortable: {
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
      cursor: 'pointer',
      transition: 'background-color 0.3s ease-in-out'
    }
  },
  selected: {
    backgroundColor: theme.palette.grey[300]
  },
  imageLarge: {
    width: theme.spacing(6),
    height: theme.spacing(6)
  }
}))
